<script lang="ts" setup>
/**
 * DashboardConfirmDialog - Standardized confirmation dialog for dashboard actions
 *
 * Usage:
 * <DashboardConfirmDialog
 *   v-model="showDialog"
 *   title="Element löschen?"
 *   description="Bist du sicher, dass du dieses Element löschen möchtest?"
 *   variant="danger"
 *   confirm-text="Löschen"
 *   :loading="isDeleting"
 *   @confirm="handleDelete"
 * >
 *   <!-- Optional custom content -->
 *   <div class="warning-note">
 *     <v-icon icon="mdi-alert" size="18" />
 *     <span>Diese Aktion kann nicht rückgängig gemacht werden.</span>
 *   </div>
 * </DashboardConfirmDialog>
 */
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title: string
  subtitle?: string
  description?: string
  icon?: string
  variant?: 'danger' | 'warning' | 'info' | 'primary'
  confirmText?: string
  cancelText?: string
  confirmIcon?: string
  loading?: boolean
  confirmDisabled?: boolean
  persistent?: boolean
  maxWidth?: string | number
}>(), {
  variant: 'danger',
  loading: false,
  confirmDisabled: false,
  persistent: false,
  maxWidth: 450
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const defaultIcon = computed(() => {
  if (props.icon) return props.icon
  switch (props.variant) {
    case 'danger': return 'mdi-alert-circle-outline'
    case 'warning': return 'mdi-alert-outline'
    case 'info': return 'mdi-information-outline'
    case 'primary': return 'mdi-help-circle-outline'
    default: return 'mdi-alert-circle-outline'
  }
})

const defaultConfirmIcon = computed(() => {
  if (props.confirmIcon) return props.confirmIcon
  switch (props.variant) {
    case 'danger': return 'mdi-delete'
    case 'warning': return 'mdi-check'
    case 'info': return 'mdi-check'
    case 'primary': return 'mdi-check'
    default: return 'mdi-check'
  }
})

const handleClose = () => {
  if (props.loading) return
  isOpen.value = false
  emit('cancel')
}

const handleConfirm = () => {
  emit('confirm')
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :max-width="maxWidth"
    :persistent="persistent || loading"
    class="dashboard-confirm-dialog"
    @update:model-value="(val) => !val && emit('cancel')"
  >
    <div class="confirm-modal" :class="`confirm-modal--${variant}`">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-icon" :class="`header-icon--${variant}`">
          <v-icon :icon="defaultIcon" size="24" />
        </div>
        <div class="header-content">
          <h2 class="modal-title">{{ title }}</h2>
          <p v-if="subtitle" class="modal-subtitle">{{ subtitle }}</p>
        </div>
        <button
          type="button"
          class="close-btn"
          :disabled="loading"
          @click="handleClose"
        >
          <v-icon icon="mdi-close" size="20" />
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <p v-if="description" class="modal-description">{{ description }}</p>
        <slot />
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="loading"
          @click="handleClose"
        >
          {{ cancelText || $t('dashboard.common.cancel') }}
        </button>
        <button
          type="button"
          class="btn"
          :class="`btn-${variant}`"
          :disabled="loading || confirmDisabled"
          @click="handleConfirm"
        >
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="18"
            width="2"
          />
          <v-icon v-else :icon="defaultConfirmIcon" size="18" />
          <span>{{ confirmText || $t('dashboard.common.confirm') }}</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.confirm-modal {
  background: rgba(15, 23, 48, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  overflow: hidden;

  &--danger {
    border-color: rgba(244, 67, 54, 0.2);
  }

  &--warning {
    border-color: rgba(255, 152, 0, 0.2);
  }
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;

  &--danger {
    background: rgba(244, 67, 54, 0.12);
    border: 1px solid rgba(244, 67, 54, 0.25);
    color: #ef5350;
  }

  &--warning {
    background: rgba(255, 152, 0, 0.12);
    border: 1px solid rgba(255, 152, 0, 0.25);
    color: #ffb74d;
  }

  &--info {
    background: rgba(33, 150, 243, 0.12);
    border: 1px solid rgba(33, 150, 243, 0.25);
    color: #64b5f6;
  }

  &--primary {
    background: rgba(0, 176, 255, 0.12);
    border: 1px solid rgba(0, 176, 255, 0.25);
    color: #00b0ff;
  }
}

.header-content {
  flex: 1;
  min-width: 0;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.modal-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 4px 0 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Content */
.modal-content {
  padding: 24px;
}

.modal-description {
  font-size: 0.95rem;
  color: rgba(200, 218, 255, 0.7);
  line-height: 1.5;
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 16px;
  }
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid transparent;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &-secondary {
    background: rgba(120, 163, 255, 0.08);
    border-color: rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.8);

    &:hover:not(:disabled) {
      background: rgba(120, 163, 255, 0.15);
      color: rgba(200, 218, 255, 0.95);
    }
  }

  &-danger {
    background: rgba(244, 67, 54, 0.15);
    border-color: rgba(244, 67, 54, 0.3);
    color: #ef5350;

    &:hover:not(:disabled) {
      background: rgba(244, 67, 54, 0.25);
      border-color: rgba(244, 67, 54, 0.45);
    }
  }

  &-warning {
    background: rgba(255, 152, 0, 0.15);
    border-color: rgba(255, 152, 0, 0.3);
    color: #ffb74d;

    &:hover:not(:disabled) {
      background: rgba(255, 152, 0, 0.25);
      border-color: rgba(255, 152, 0, 0.45);
    }
  }

  &-info {
    background: rgba(33, 150, 243, 0.15);
    border-color: rgba(33, 150, 243, 0.3);
    color: #64b5f6;

    &:hover:not(:disabled) {
      background: rgba(33, 150, 243, 0.25);
      border-color: rgba(33, 150, 243, 0.45);
    }
  }

  &-primary {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00b0ff;

    &:hover:not(:disabled) {
      background: rgba(0, 176, 255, 0.25);
      border-color: rgba(0, 176, 255, 0.45);
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .confirm-modal {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(28, 110, 227, 0.15);

    &--danger {
      border-color: rgba(244, 67, 54, 0.2);
    }

    &--warning {
      border-color: rgba(255, 152, 0, 0.2);
    }
  }

  .modal-header {
    border-bottom-color: rgba(28, 110, 227, 0.1);
  }

  .modal-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .modal-subtitle {
    color: rgba(15, 23, 42, 0.5);
  }

  .close-btn {
    color: rgba(15, 23, 42, 0.5);

    &:hover:not(:disabled) {
      background: rgba(28, 110, 227, 0.08);
      color: rgba(15, 23, 42, 0.8);
    }
  }

  .modal-description {
    color: rgba(15, 23, 42, 0.7);
  }

  .modal-footer {
    border-top-color: rgba(28, 110, 227, 0.1);
  }

  .btn-secondary {
    background: rgba(28, 110, 227, 0.06);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.7);

    &:hover:not(:disabled) {
      background: rgba(28, 110, 227, 0.1);
      color: rgba(15, 23, 42, 0.9);
    }
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .close-btn,
  .btn {
    transition: none;
  }
}
</style>
